import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import Button from '../../../components/ui/Button'
import { rowActionLabel } from '../../../lib/deleteLabel'
import type { TodoList } from '../types'

interface Props {
  list: TodoList
  /** İşlem süren liste — düğmeler kilitlenir (çift istek olmasın). */
  busy: boolean
  onRename: (list: TodoList) => void
  /**
   * Paylaşım diyaloğu — **US2'de** bağlanır. Verilmediğinde paylaş düğmesi hiç çizilmez:
   * çalışmayan (ya da başka bir şey yapan) bir düğme göstermek, kullanıcıya olmayan bir yetenek
   * vaat ederdi.
   */
  onShare?: (list: TodoList) => void
  onDelete: (list: TodoList) => void
  /**
   * Kısıtlı Yetkili asistan kipi (126). `true` iken **yeniden adlandır ve sil düşer**; paylaşım
   * düğmesi DURUR, çünkü listenin kimlerle paylaşıldığını görmek bir okumadır — pop-up kendi
   * içinde salt-okumaya geçer.
   */
  readOnly?: boolean
}

/**
 * Notlarım ana ekranındaki liste kartı (113).
 *
 * <p><b>overlay-link biçimi</b> (anayasa → Page Standards): kart kökü handler taşımayan düz bir
 * `div`'dir, tıklanabilirliği başlıktaki **gerçek** `<Link>` verir ve o bağlantı kartı `::after` ile
 * kaplar; eylem düğmeleri bağlantının **kardeşidir**. Kart köküne `role="button"` koyup içine düğme
 * yerleştirmek ARIA'da geçersizdir (interaktif içinde interaktif) ve THY incelemesinde bulgu olarak
 * çıkmıştı.
 *
 * <p><b>Eylem adları satır adını taşır</b> (`rowActionLabel`): sesli kontrol kullanıcısı için
 * düğmenin tek tanımlayıcısı erişilebilir addır — üç listede üç kez "Sil" yazsaydı hangisinin
 * kastedildiği anlaşılmazdı. `title` de aynı değeri alır.
 *
 * <p><b>Aksiyonlar HER görünen listede çizilir</b> (30.09.2026): paylaşılan kullanıcı o listede
 * sahibinin yapabildiği her işlemi yapar, dolayısıyla yeniden adlandırma/paylaşma/silme düğmeleri
 * ondan da gizlenmez. Eskiden `owned === false` iken hiçbiri çizilmiyordu.
 *
 * <p><b>SAHİPLİK koşulu KALDIRILDI, `owned` ile DEĞİŞTİRİLMEDİ</b>: sunucu listeyi görebilen
 * herkesin yazmasına izin veriyor, yani sahipliğe dayanan bir gizleme istemcide gereksizdi — o
 * koşulu bırakmak, sunucuyla ayrışabilecek ikinci bir kural demek olurdu. `owned` bugün yalnız
 * "Liste sahibi: …" satırını çizmek için okunur.
 *
 * <p><b>Ama koşulsuz da değil: Kısıtlı Yetkili asistan</b> (126, 21.10.2026) yeniden adlandırma ve
 * silmeyi görmez — bkz. {@code readOnly} prop'u. Yukarıdaki cümleyle çelişmemesinin sebebi, ölçütün
 * <b>sahiplik olmaması</b>dır: persona kipi sunucunun kendi kuralıyla (GET dışı her isteğin reddi)
 * aynı yönde çalışır, ona rakip bir kural kurmaz.
 */
export default function TodoListCard({
  list,
  busy,
  onRename,
  onShare,
  onDelete,
  readOnly = false,
}: Readonly<Props>) {
  const { t } = useTranslation()

  /** Paylaşım sayısı varsa erişilebilir ad da onu söyler — rozet görsel, ad sesli karşılığıdır. */
  const shareLabel =
    list.shareCount > 0
      ? t('todo.shareNamedCount', { name: list.name, count: list.shareCount })
      : rowActionLabel(t, 'todo.share', list.name)

  return (
    <div className="ui-card ui-card--linked todo-card">
      <div className="ui-card-top">
        {/* "2/5" görsel olarak yeterli, sesli olarak DEĞİL: ekran okuyucu kullanıcısı bu iki
            sayının ne olduğunu bilemez (THY inceleme bulgusu, 25.09.2026). Görsel katman
            `aria-hidden`, sesli karşılığı görsel-gizli bir cümle — aynı ayrım rozette de var
            (bkz. aşağıdaki `todo-share-badge`). */}
        <span className="todo-card-count">
          <span aria-hidden="true">
            {list.completedTaskCount}/{list.taskCount}
          </span>
          <span className="ui-sr-only">
            {t('todo.taskProgress', {
              completed: list.completedTaskCount,
              total: list.taskCount,
            })}
          </span>
        </span>
        <span className="ui-card-actions ui-card-actions--compact">
          {!readOnly && (
            <Button
              icon="pi pi-pencil"
              rounded
              text
              disabled={busy}
              aria-label={rowActionLabel(t, 'todo.renameList', list.name)}
              title={rowActionLabel(t, 'todo.renameList', list.name)}
              onClick={() => onRename(list)}
            />
          )}
          {onShare && (
            /* Rozet düğmenin KARDEŞİ, içeriği değil: düğmenin içine konan bir metin erişilebilir
               adın parçası olur ve "Ev listesini paylaş 2" gibi bir ad üretirdi. Sayı bunun
               yerine `aria-hidden` bir katman olarak çizilir, ekran okuyucuya ise düğmenin kendi
               adıyla düzgün bir cümle hâlinde verilir. */
            <span className="todo-share-action">
              <Button
                icon="pi pi-share-alt"
                rounded
                text
                disabled={busy}
                aria-label={shareLabel}
                title={shareLabel}
                onClick={() => onShare(list)}
              />
              {/* Sıfırsa rozet HİÇ çizilmez — "0" yazan bir rozet bilgi taşımaz (emsal:
                  bildirim çanı `.nbadge`). */}
              {list.shareCount > 0 && (
                <span className="todo-share-badge" aria-hidden="true">
                  {list.shareCount}
                </span>
              )}
            </span>
          )}
          {!readOnly && (
            <Button
              icon="pi pi-trash"
              rounded
              text
              severity="danger"
              disabled={busy}
              aria-label={rowActionLabel(t, 'todo.deleteList', list.name)}
              title={rowActionLabel(t, 'todo.deleteList', list.name)}
              onClick={() => onDelete(list)}
            />
          )}
        </span>
      </div>
      <div className="ui-card-head">
        {/* `title`: başlık iki satırda kırpıldığı için tam ad üzerine gelince görünür (T030).
            Ekran okuyucu zaten metnin tamamını okur — `title` yalnız görsel kullanıcı içindir. */}
        <Link
          className="ui-card-title ui-card-link"
          to={`/minutes/todo-lists/${list.id}`}
          title={list.name}
        >
          {list.name}
        </Link>
      </div>
      {/* Sahip bilgisi YALNIZ paylaşılan listelerde: kendi listesinde gereksiz bir ad, "kendi listem
          / paylaşılan" ayrımını bulanıklaştırırdı (aynı adlı iki liste olabilir). */}
      {!list.owned && list.ownerFullName && (
        <span className="ui-card-sub">
          {t('todo.ownerPrefix')}: {list.ownerFullName}
        </span>
      )}
    </div>
  )
}
